import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface Client {
  line_2: string;
  line_1: string;
  client_last_name: string;
  client_first_name: string;
  client_id?: number;
  client_name: string;
  contact_person: string;
  contact_number: string;
  state: string;
  district: string;
  sub_district: string;
  villages: string;
  khasra_no?: string;
  area?: string;
  type?: string;
  org_id?: number;
  created_at?: string;
  updated_at?: string;
  address?: string;
}

export interface ClientResponse {
  status: string;
  data: Client[];
  count?: number;
  message?: string;
}

export interface SingleClientResponse {
  status: string;
  data: Client;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ClientService {
  private apiUrl = `${environment.BASE_URL}client`;

  constructor(private http: HttpClient) { }

  // Get all clients
  getClients(): Observable<ClientResponse> {
    return this.http.get<ClientResponse>(this.apiUrl);
  }

  // Get client by ID
  getClient(id: number): Observable<SingleClientResponse> {
    return this.http.get<SingleClientResponse>(`${this.apiUrl}/${id}`);
  }

  // Create new client
  createClient(client: Client): Observable<SingleClientResponse> {
    return this.http.post<SingleClientResponse>(this.apiUrl, client);
  }

  // Update client
  updateClient(id: number, client: Client): Observable<SingleClientResponse> {
    return this.http.put<SingleClientResponse>(`${this.apiUrl}/${id}`, client);
  }

  // Delete client
  deleteClient(id: number): Observable<SingleClientResponse> {
    return this.http.delete<SingleClientResponse>(`${this.apiUrl}/${id}`);
  }

  // Utility methods
  getTypeText(type: string): string {
    switch (type?.toLowerCase()) {
      case 'individual':
        return 'Individual';
      case 'corporate':
        return 'Corporate';
      case 'government':
        return 'Government';
      default:
        return type || 'Individual';
    }
  }

  getTypeClass(type: string): string {
    switch (type?.toLowerCase()) {
      case 'individual':
        return 'type-individual';
      case 'corporate':
        return 'type-corporate';
      case 'government':
        return 'type-government';
      default:
        return 'type-individual';
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getApiErrorMessage(error: any, fallback: string): string {
    const body = error?.error;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    return body?.message || body?.errorMessage || body?.error || error?.message || fallback;
  }

  formatPhoneNumber(phone: string): string {
    if (!phone) return '';
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    // Format as XXX-XXX-XXXX
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return phone;
  }

  validateClient(client: Client): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!client.client_name?.trim()) {
      errors.push('Client name is required');
    }

    if (!client.contact_person?.trim()) {
      errors.push('Contact person is required');
    }

    if (!client.contact_number?.trim()) {
      errors.push('Contact number is required');
    }

    if (!client.state?.trim()) {
      errors.push('State is required');
    }

    if (!client.district?.trim()) {
      errors.push('District is required');
    }

    if (!client.sub_district?.trim()) {
      errors.push('Sub-district is required');
    }

    if (!client.villages?.trim()) {
      errors.push('Villages is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

